import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { InsuranceQuote } from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';
import { ReadOnlyQuill } from 'modules/desking/ui/components/catalogDetails/ReadOnlyQuill';
import { convertHtmlToDelta } from 'utils/quillUtils';

interface Props {
  insuranceQuote: Nullable<InsuranceQuote>;
  onEnterRequirements: () => void;
}

export const LenderRequirementsDisplayWithManualInsuranceQuoteData = ({
  insuranceQuote,
  onEnterRequirements,
}: Props) => {
  const classes = useStyles();

  const coverageHtml = insuranceQuote?.insuranceCoverage?.coverageRequirements ?? '<p>—</p>';
  const certificateHtml = insuranceQuote?.insuranceCoverage?.certificateRequirements ?? '<p>—</p>';
  const otherHtml = insuranceQuote?.insuranceCoverage?.otherInstructions ?? '<p>—</p>';

  return (
    <Box className={classes.shell}>
      <Box className={classes.topBar}>
        <Box className={classes.topBarRight}>
          <Button
            className={classes.topBarBtn}
            variant="outlined"
            onClick={() => {
              onEnterRequirements();
            }}
          >
            EDIT INSURANCE REQUIREMENTS
          </Button>
        </Box>
      </Box>

      {/* White content area */}
      <Box className={classes.body}>
        <Typography className={classes.sectionTitle}>Insurance Requirements</Typography>

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Coverage Requirements</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(coverageHtml)} />
          </Box>
        </Box>

        <Divider className={classes.thinDivider} />

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Certificate Requirements</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(certificateHtml)} />
          </Box>
        </Box>

        <Divider className={classes.thinDivider} />

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Other Instructions</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(otherHtml)} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  shell: {
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },

  // gray header strip
  topBar: {
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  topBarIcon: {
    width: 26,
    height: 26,
    borderRadius: 4,
    backgroundColor: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  topBarIconSvg: {
    fontSize: 18,
    color: '#6b7280',
  },
  topBarTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#111827',
    whiteSpace: 'nowrap',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  topBarBtn: {
    borderColor: '#93c5fd',
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    minHeight: 28,
    textTransform: 'none',
    borderRadius: 4,
  },

  body: {
    padding: 14,
    backgroundColor: '#ffffff',
  },
  pageTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    marginBottom: 10,
  },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    alignItems: 'start',
  },
  col: {
    minWidth: 0,
  },
  colTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
  },

  kvRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'baseline',
    marginBottom: 4,
  },
  kLabel: {
    fontSize: 11,
    color: '#6b7280',
  },
  kValue: {
    fontSize: 11,
    color: '#111827',
  },

  contactName: {
    fontSize: 11,
    color: '#111827',
    marginBottom: 4,
  },
  contactLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  contactIcon: {
    fontSize: 14,
    color: '#111827',
  },
  contactText: {
    fontSize: 11,
    color: '#111827',
  },

  divider: {
    margin: '12px 0',
    borderColor: '#e5e7eb',
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 8,
  },

  requirementBlock: {
    padding: '6px 0',
  },
  blockTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
  },
  thinDivider: {
    margin: '8px 0',
    borderColor: '#e5e7eb',
  },

  richText: {
    fontSize: 11,
    color: '#111827',
    '& .ql-container': {
      border: 'none',
    },
    '& .ql-editor': {
      padding: 0,
      fontSize: 11,
      lineHeight: 1.4,
      color: '#111827',
    },
    '& .ql-editor p': {
      margin: '0 0 6px 0',
    },
    '& .ql-editor ul': {
      margin: '0 0 6px 18px',
      padding: 0,
    },
    '& .ql-editor li': {
      marginBottom: 4,
    },
  },

  '@media (max-width: 900px)': {
    twoCol: {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  },
});
